import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Sender, Bubble, useXAgent, useXChat} from "@ant-design/x";
import {NumberFormat} from "../../../components/DesignSystem/NumberFormat/NumberFormat";
import {ResponsePlot} from "../../../components/AiAssistant/MessageImage/ResponsePlot";
import {ResponseDataFrame} from "../../../components/AiAssistant/MessageTable/ResponseDataFrame";
import SmileOutlined from "@ant-design/icons/lib/icons/SmileOutlined";
import {Button, message, Space} from "antd";
import {BulbOutlined} from "@ant-design/icons";
import {assistantService} from "../../../services/assistantService";
import {SuggestionsModal} from "./SuggestionsModal";
import {Spinner} from "../../../components/DesignSystem/Spinner/Spinner";
import {isDisabled} from "../../../utils/security.utils";

export const AIAssistantChat = ({user, objectId, objectType, onAsk, sheetName, history}) => {

    const [content, setContent] = React.useState('');
    const [name, setName] = React.useState(sheetName);
    const [suggestions, setSuggestions] = React.useState(undefined);
    const [showSuggestionsDialog, setShowSuggestionsDialog] = React.useState(false);

    const [loadingContext, setLoadingContext] = useState({loading: false, tip: undefined});


    const roles = {
        ai: {
            placement: 'start',
        },
        local: {
            placement: 'end'
        },
    };

    const nameRef = useRef(name);

    useEffect(() => {
        nameRef.current = name;
    }, [name]);

    const [agent] = useXAgent({
        request: async ({message}, {onSuccess, onError}) => {
            onAsk(message, nameRef.current, onSuccess, onError);
        }
    });

    // Chat messages
    const {onRequest, messages} = useXChat({
        agent,
        requestPlaceholder: 'Waiting...',
        requestFallback: 'We’re having trouble to process your query. Please try rephrasing it, or contact support if the issue continues.',
    });

    const handleSuggestion = () => {

        if (suggestions) {
            setShowSuggestionsDialog(true);
            return;
        }

        setLoadingContext({loading: true, tip: 'Loading suggestions...'});

        assistantService.getSuggestions(objectType, objectId, sheetName).then(response => {
            setSuggestions(response.data);
            setShowSuggestionsDialog(true);
        }).catch(error => {
            message.error('Error fetching suggestions');
            setShowSuggestionsDialog(false);
        }).finally(() => {
            setLoadingContext({loading: false});
        });

    }

    const handleSelectedSuggestion = (value) => {
        setShowSuggestionsDialog(false);
        onRequest(value, nameRef.current, (response) => {});
    }

    const handleCloseSuggestionDialog = () => {
        setShowSuggestionsDialog(false);
    }

    const getVariant = (role, status) => {

        if (role === 'ai' || role === 'local') {
            return role === 'ai' ? 'borderless' : 'filled'
        } else {
            return status === 'local' ? 'filled' : 'borderless'
        }

    }

    const getRole = (role, status) => {

        if (role === 'ai' || role === 'local') {
            return role;
        } else {
            return status === 'local' ? 'local' : 'ai';
        }

    }

    const items = useMemo(() => {

        const joinMessages = history ? history.concat(messages) : messages;

        return joinMessages.map(({id, message, status, role}) => ({
            key: id,
            loading: status === 'loading',
            role: getRole(role, status),
            variant: getVariant(role, status),
            content: formatMessage(message, objectType, objectId),
            icon: <SmileOutlined style={{color: '#FAAD14'}}/>
        }));
    }, [messages, history]); // eslint-disable-line

    useEffect(() => {
        setName(sheetName);
    }, [sheetName]);

    return <Spinner spinning={loadingContext.loading} tip={loadingContext.tip}>
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '95vh',
            paddingLeft: '20px',
            paddingRight: '20px',
        }}>
            <div style={{flex: 1, overflow: 'auto', paddingBottom: '20px', paddingTop: '20px'}}>
                <Bubble.List
                    roles={roles}
                    style={{
                        maxHeight: '90%',
                        minHeight: '90%',
                    }}
                    items={items}
                />
            </div>

            {showSuggestionsDialog &&
                <SuggestionsModal suggestions={suggestions} onSelect={handleSelectedSuggestion} onClose={handleCloseSuggestionDialog}/>}

            <Sender loading={agent.isRequesting()} style={{marginTop: 'auto'}}
                    value={content}
                    onChange={setContent}
                    allowSpeech={true}
                    disabled={isDisabled(user)}
                    placeholder="How can I help you?"
                    actions={(_, info) => {
                        const {SendButton, SpeechButton} = info.components;
                        return (
                            <Space size="small">
                                <Button type={"text"} icon={<BulbOutlined/>} onClick={handleSuggestion} disabled={isDisabled(user)}/>
                                <SpeechButton disabled={isDisabled(user)}/>
                                <SendButton type="primary" disabled={isDisabled(user)}/>
                            </Space>
                        );
                    }}
                    onSubmit={(nextContent) => {
                        onRequest(nextContent);
                        setContent('');
                    }}
            />
        </div>
    </Spinner>

}

const formatMessage = (message, objectType, objectId) => {

    if (typeof message === 'string') {
        return <>{message}</>
    } else if (message.type === 'plot') {
        return <>{showChart(message, objectType, objectId)}</>
    } else if (message.type === 'dataframe') {
        return <ResponseDataFrame value={message.value} context={message.context} objectType={objectType} objectId={objectId}/>
    } else {
        return <div>{message.value}</div>
    }

}

const showChart = (message, objectType, objectId) => <ResponsePlot imageName={message.value} objectType={objectType} objectId={objectId}/>

export const formatValue = (type, value) => {

    if (type === 'integer' || type === 'number') {
        return <NumberFormat value={value}/>
    }

    return value;
}